import React from 'react'
import { TextField as UITextField } from '@carsys/ui'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import { useMaskedValue } from '@carsys/form/src/utils/useMaskedValue'
import { isInvalid } from './utils'
import { FieldHookConfig, useField } from 'formik'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'

interface IOwnProps {
  precision?: number
  allowNegative?: boolean
  divisible?: boolean
}

interface NumericInputProps<Value>
  extends Omit<React.ComponentProps<typeof UITextField>, 'component' | 'defaultChecked' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'>,
    IOwnProps {}

const toFloat = (input: string) => {
  if (input === '') {
    return 0
  }
  return parseFloat(input.toString().replace(',', '.'))
}

export function NumericInput<Value>({ name, validate, precision = 0, allowNegative, divisible, ...props }: NumericInputProps<Value>) {
  const [input, meta, helpers] = useField({ name, validate })
  useRegisterFormField(name)

  const { formatterNoCurrency } = React.useContext(LocaleContext)
  const [originalValue, setOriginalValue] = React.useState(input.value || '')
  const regexp = React.useMemo(() => {
    let regex = '^'
    if (allowNegative) {
      regex += '-?'
    }
    regex += '\\d*?'
    if (divisible) {
      regex += '[\\d.,]*?'
    }
    if (precision > 0) {
      regex += `[\\.,]?\\d{0,${precision}}`
    }
    regex += '$'
    return new RegExp(regex)
  }, [precision, allowNegative])

  const changeHandler = (e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (regexp.test(value) || value === '') {
      setOriginalValue(value)

      helpers.setValue(toFloat(value))
    }
  }

  const handleFocus = () => {
    setOriginalValue(parseFloat(originalValue) ? originalValue : '')
  }

  const [value, events, isEditing] = useMaskedValue(
    input.value ?? 0,
    value => {
      if (!isEditing) {
        setOriginalValue(value)
      }
      const formatted = formatterNoCurrency.format(toFloat(value))
      return formatted
    },
    { onChange: changeHandler, onBlur: input.onBlur, onFocus: () => handleFocus() }
  )

  return (
    <UITextField
      id={input.name}
      error={isInvalid(meta)}
      {...props}
      {...input}
      {...events}
      value={isEditing ? originalValue : value}
      helperText={isInvalid(meta) ? meta.error : props.helperText}
      autoComplete='none'
    />
  )
}
